/** ONE TAP MARKS EVERY PROPERTY OF OURS (TW-0443, 2026-11-04).
 *
 *  Chris, 2026-10-22, after the admin Home's "Collectors: 1" turned out to be his own test: "you're welcome to make sure i'm
 *  removed from any of these beacons, assuming i'm not." Until this file the mark lived in localStorage, which is per ORIGIN,
 *  so `?wt_internal=1` on www marked www alone: app.waivers.tattoo, every shop's form (`<shop>.waivers.tattoo`), every shop's
 *  link page, booking page, deposit page and website each wanted their own tap, and the one nobody remembered counted us.
 *
 *  THE MARK is a cookie `wt_internal=1` on a registrable domain of ours (MARK_DOMAINS: `.waivers.tattoo`, `.flashes.tattoo`,
 *  `.l.tattoo`, `.deposits.tattoo`, `.bookings.tattoo`, `.websites.tattoo`; host-only anywhere else: localhost, a preview),
 *  Path=/, a year, SameSite=Lax, Secure on https, OR this origin's localStorage. A browser is ours when EITHER says so.
 *
 *  WHO SETS THE COOKIE: ONLY A VERIFIED HOP OF THE ADMIN'S CHAIN (followMarkChain, below; Chris, 2026-11-04, asking whether he'd
 *  have to "click through something unguessable so my browser gets marked on all of the properties"). One tap in the admin
 *  mints a token and sends the browser through `https://www.<domain>/?wt_mark=<token>&wt_next=<the origins left>&wt_back=<the
 *  admin>&wt_took=<the domains that took it>`, one hop a domain, every hop on the site (apps/marketing serves www of all six).
 *  Each hop asks the admin's API whether the token is live (MARK_VERIFY_URL, a constant, never read from the address) BEFORE
 *  it sets the cookie, then moves on; the last hop lands back on the admin with `marked=<the domains that took it>`. So a
 *  stranger can neither hide from our counts across our domains by typing a parameter, nor be marked by a link of ours.
 *
 *  `?wt_internal=1`, the obvious parameter, marks THIS ORIGIN'S STORAGE ALONE, as it did before TW-0443 (companion scripts and
 *  walks use it; a scripted browser is marked by navigator.webdriver anyway). `?wt_internal=0` clears both the cookie and the
 *  storage. Any other value, and no parameter at all, leaves the mark as it stands.
 *
 *  THE ONE SOURCE. `./run.sh sync-shared` copies it into apps/marketing/components/shared/ and apps/app/components/shared/,
 *  whose beacons (lib/beacon.ts) read it, and byte-for-byte to src/internal-mark.ts in the waiver form (a create-react-app
 *  tree that compiles only its own src/; apps/form/src/internal.test.ts holds it) and in the four Vite pages (apps/link-page,
 *  apps/booking-page, apps/deposit-page, apps/website, whose our-own-browser.ts reads it; each page's beacon test holds it).
 *  `./run.sh sync-shared --check` says whether every copy matches. No imports, so every compiler takes it as it is.
 *
 *  IT IS A HINT AND NEVER A GATE, and it never throws: every cookie, storage and network access is in its own try, and a
 *  browser that refuses both is a person's, the safe way round (a client whose browser blocks site data is still counted). */

/** The family's mark: the key in storage, the cookie's name and the address's parameter. */
export const INTERNAL_KEY = 'wt_internal';

/** A year, in seconds: the cookie's Max-Age. */
export const MARK_MAX_AGE_S = 365 * 24 * 60 * 60;

/** The registrable domains whose hosts share one mark, one hop of the admin's chain each. Each is its own: a cookie on one never
 *  reaches another, which is why the chain visits every one. */
export const MARK_DOMAINS: readonly string[] = [
  'waivers.tattoo',
  'flashes.tattoo',
  'l.tattoo',
  'deposits.tattoo',
  'bookings.tattoo',
  'websites.tattoo',
];

/** The three methods of a storage this needs, so a test can hand it a plain object. */
export type MarkStore = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** A cookie jar: what `document.cookie` reads and takes, so a test can hand it a plain object. */
export type MarkJar = { read: () => string; write: (cookie: string) => void };

/** Where the page is: its hostname, and whether it is served over https (the cookie is Secure only there). */
export type MarkHere = { host: string; https: boolean };

/** The registrable domain of ours a host is, or is under (`deposits.tattoo` for `demo.deposits.tattoo`; `l.tattoo` for
 *  `Name.L.tattoo`), or null for every other host. Lowercase, a trailing dot dropped. */
export function markDomainOf(host: string): string | null {
  const h = String(host ?? '').trim().toLowerCase().replace(/\.$/, '');
  for (const d of MARK_DOMAINS) if (h === d || h.endsWith(`.${d}`)) return d;
  return null;
}

/** The Domain attribute for a host: `.waivers.tattoo` for waivers.tattoo and every host under it, the same for each of
 *  MARK_DOMAINS, and null (a host-only cookie, no Domain attribute) for every other host. */
export function markCookieDomain(host: string): string | null {
  const d = markDomainOf(host);
  return d ? `.${d}` : null;
}

/** The cookie string that sets (on) or clears (off) the mark for a page on this host. `hostOnly` drops the Domain attribute,
 *  for the clearing pass that also lifts a host-only cookie set before this rule. */
export function markCookie(on: boolean, here: MarkHere, hostOnly = false): string {
  const domain = hostOnly ? null : markCookieDomain(here.host);
  return [
    `${INTERNAL_KEY}=${on ? '1' : ''}`,
    domain ? `Domain=${domain}` : '',
    'Path=/',
    `Max-Age=${on ? MARK_MAX_AGE_S : 0}`,
    'SameSite=Lax',
    here.https ? 'Secure' : '',
  ].filter(Boolean).join('; ');
}

/** Does a Cookie header (or `document.cookie`) carry the mark, exactly `wt_internal=1`? */
export function cookieMarked(cookie: string | null | undefined): boolean {
  for (const part of String(cookie ?? '').split(';')) {
    const eq = part.indexOf('=');
    if (eq < 0) continue;
    if (part.slice(0, eq).trim() === INTERNAL_KEY && part.slice(eq + 1).trim() === '1') return true;
  }
  return false;
}

/** This origin's localStorage, or null where the browser refuses it (reading it can THROW, not just return null). */
export function browserStore(): MarkStore | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

/** This page's cookies, or null where there is no document (the server, a test on the host). */
export function browserJar(): MarkJar | null {
  try {
    if (typeof document === 'undefined') return null;
    return {
      read: () => document.cookie,
      write: (cookie: string) => {
        document.cookie = cookie;
      },
    };
  } catch {
    return null;
  }
}

/** Where this page is, or nowhere (an empty host: a host-only cookie) where there is no location. */
export function browserHere(): MarkHere {
  try {
    // `globalThis.location`, not the bare global: the form's build (react-scripts, eslint's no-restricted-globals)
    // refuses the bare name, and the node tests stub the location on globalThis, where a browser's window is too.
    const here = (globalThis as { location?: { hostname: string; protocol: string } }).location;
    if (!here) return { host: '', https: false };
    return { host: here.hostname, https: here.protocol === 'https:' };
  } catch {
    return { host: '', https: false };
  }
}

/** Is this browser marked as ours, by the cookie OR by this origin's storage? */
export function isMarked(store: MarkStore | null = browserStore(), jar: MarkJar | null = browserJar()): boolean {
  let stored = false;
  try {
    stored = store?.getItem(INTERNAL_KEY) === '1';
  } catch {
    stored = false;
  }
  if (stored) return true;
  try {
    return !!jar && cookieMarked(jar.read());
  } catch {
    return false;
  }
}

/** Mark (or unmark) this browser: the cookie on the registrable domain and this origin's storage, each in its own try. */
export function setMark(
  on: boolean,
  store: MarkStore | null = browserStore(),
  jar: MarkJar | null = browserJar(),
  here: MarkHere = browserHere(),
): void {
  try {
    if (store) {
      if (on) store.setItem(INTERNAL_KEY, '1');
      else store.removeItem(INTERNAL_KEY);
    }
  } catch {
    /* storage blocked: the cookie below still marks */
  }
  try {
    if (!jar) return;
    jar.write(markCookie(on, here));
    if (!on && markCookieDomain(here.host)) jar.write(markCookie(false, here, true));
  } catch {
    /* cookies blocked: storage above still marks this origin */
  }
}

/** `?wt_internal=1` on the page's address marks THIS ORIGIN'S STORAGE alone (never the cookie: only a verified hop of the admin's
 *  chain sets that); `?wt_internal=0` clears both the cookie and the storage. Any other value, and no parameter at all, leaves
 *  the mark exactly as it stands, so a client arriving from a QR code can neither set one nor clear one. */
export function readMarkFromSearch(
  search: string,
  store: MarkStore | null = browserStore(),
  jar: MarkJar | null = browserJar(),
  here: MarkHere = browserHere(),
): void {
  try {
    const v = new URLSearchParams(search).get(INTERNAL_KEY);
    if (v === '1') {
      try {
        store?.setItem(INTERNAL_KEY, '1');
      } catch {
        /* storage blocked: nothing to remember with */
      }
    } else if (v === '0') setMark(false, store, jar, here);
  } catch {
    /* a malformed address leaves the mark as it stands */
  }
}

// ── THE ADMIN'S CHAIN: one tap marks every domain (TW-0443, 2026-11-04) ─────────────────────────────────────────────────────

/** The address parameters of a hop. */
export const CHAIN_PARAMS = { token: 'wt_mark', next: 'wt_next', back: 'wt_back', took: 'wt_took' } as const;

/** Where a hop asks whether the admin minted this token: 200 `{"ok":true}` for a live one, 403 `{"ok":false}` otherwise. A
 *  CONSTANT, never read from the address: a stranger could point an address at a server of their own. */
export const MARK_VERIFY_URL = 'https://api.funeral.link/api/v0/internal-mark/verify';

/** How long a hop waits on the answer before it moves on unmarked. */
export const MARK_VERIFY_TIMEOUT_MS = 5000;

/** The admin hosts a chain may return to (https, no port); a dev admin on http://localhost:<port> too. */
const ADMIN_HOSTS = ['admin.waivers.tattoo', 'admin.funeral.link'];

/** A token as the admin mints it (a Phoenix.Token: `SFMyNTY.<payload>.<signature>`, url-safe base64 joined by dots), long
 *  enough not to be guessed and short enough to be nothing else. */
const TOKEN = /^[A-Za-z0-9_.-]{16,512}$/;

/** A hop, read off its address: the token, the hop ORIGINS still to visit, the admin address to land on, the domains that
 *  took the mark so far. */
export type MarkChain = { token: string; next: string[]; back: string; took: string[] };

/** Does this address carry a hop at all (any `wt_mark`, valid or not)? A page that does is ours for its one load: the site's
 *  beacon and its ad tags leave it out (a stranger who types it hides one page view, and keeps nothing). */
export function markChainInAddress(search: string): boolean {
  try {
    return new URLSearchParams(search).has(CHAIN_PARAMS.token);
  } catch {
    return false;
  }
}

/** The admin address a chain may return to, as given, or null: https on exactly admin.waivers.tattoo or admin.funeral.link (no
 *  port, no user), or http on localhost (any port) for a dev admin. */
export function validMarkBack(raw: string | null | undefined): string | null {
  try {
    if (!raw) return null;
    const u = new URL(raw);
    if (u.username || u.password) return null;
    const host = u.hostname.toLowerCase();
    if (u.protocol === 'https:' && !u.port && ADMIN_HOSTS.includes(host)) return u.toString();
    if (u.protocol === 'http:' && host === 'localhost') return u.toString();
    return null;
  } catch {
    return null;
  }
}

/** A next hop as its ORIGIN (`https://www.deposits.tattoo`), or null: https, no port, no user, no path but `/`, no query or
 *  fragment, and a host that is or is under one of MARK_DOMAINS (any case: `www.L.tattoo` arrives as either). */
export function validMarkHop(raw: string | null | undefined): string | null {
  try {
    const t = String(raw ?? '').trim();
    if (!t) return null;
    const u = new URL(t);
    if (u.protocol !== 'https:' || u.port || u.username || u.password) return null;
    if (u.pathname !== '/' || u.search || u.hash || /[?#]/.test(t)) return null;
    if (!markDomainOf(u.hostname)) return null;
    return u.origin;
  } catch {
    return null;
  }
}

/** The hop an address carries, or null when it carries none or anything in it is not what the admin sends: then a hop does
 *  nothing at all, no cookie and no redirect. */
export function readMarkChain(search: string): MarkChain | null {
  try {
    const q = new URLSearchParams(search);
    const token = q.get(CHAIN_PARAMS.token);
    if (token === null || !TOKEN.test(token)) return null;
    const back = validMarkBack(q.get(CHAIN_PARAMS.back));
    if (!back) return null;
    const rawNext = (q.get(CHAIN_PARAMS.next) ?? '').split(',').map((x) => x.trim()).filter(Boolean);
    if (rawNext.length > 20) return null;
    const next: string[] = [];
    for (const n of rawNext) {
      const o = validMarkHop(n);
      if (!o) return null;
      next.push(o);
    }
    const took: string[] = [];
    for (const d of (q.get(CHAIN_PARAMS.took) ?? '').split(',')) {
      const t = d.trim().toLowerCase();
      if ((MARK_DOMAINS.includes(t) || t === 'localhost') && !took.includes(t)) took.push(t);
    }
    return { token, next, back, took };
  } catch {
    return null;
  }
}

/** Where a hop goes after it has done its part: the next origin's `/` carrying the chain on, or, when none remain, the admin
 *  address with `marked=<the domains that took it>` added to the query it already has. */
export function nextMarkAddress(chain: MarkChain, took: string[]): string {
  if (chain.next.length > 0) {
    const [first, ...rest] = chain.next;
    const q = new URLSearchParams();
    q.set(CHAIN_PARAMS.token, chain.token);
    q.set(CHAIN_PARAMS.next, rest.join(','));
    q.set(CHAIN_PARAMS.back, chain.back);
    q.set(CHAIN_PARAMS.took, took.join(','));
    return `${first}/?${q.toString()}`;
  }
  const u = new URL(chain.back);
  u.searchParams.set('marked', took.join(','));
  return u.toString();
}

/** What a hop needs of fetch: a status and a JSON body. */
export type MarkFetch = (
  url: string,
  init?: { method?: string; credentials?: 'omit'; cache?: 'no-store'; mode?: 'cors'; signal?: AbortSignal },
) => Promise<{ status: number; json: () => Promise<unknown> }>;

/** Did the admin mint this token? True ONLY on a 200 whose body is `{"ok":true}` inside the time allowed; a refusal, a
 *  timeout, a network failure or a body of any other shape is false. Never throws. */
export async function verifyMarkToken(
  token: string,
  fetchFn: MarkFetch | null = typeof fetch === 'undefined' ? null : (fetch as unknown as MarkFetch),
  timeoutMs: number = MARK_VERIFY_TIMEOUT_MS,
): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    if (!fetchFn) return false;
    const ctl = typeof AbortController === 'undefined' ? null : new AbortController();
    const timedOut = new Promise<false>((resolve) => {
      timer = setTimeout(() => {
        try {
          ctl?.abort();
        } catch {
          /* nothing to abort */
        }
        resolve(false);
      }, timeoutMs);
    });
    const asked = (async () => {
      const res = await fetchFn(`${MARK_VERIFY_URL}?token=${encodeURIComponent(token)}`, {
        method: 'GET',
        credentials: 'omit',
        cache: 'no-store',
        mode: 'cors',
        ...(ctl ? { signal: ctl.signal } : {}),
      });
      if (res.status !== 200) return false;
      const body = (await res.json()) as { ok?: unknown } | null;
      return !!body && body.ok === true;
    })().catch(() => false);
    return (await Promise.race([asked, timedOut])) === true;
  } catch {
    return false;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/** Everything a hop touches, each one injectable for a test. */
export type MarkChainDeps = {
  search: string;
  here: MarkHere;
  store: MarkStore | null;
  jar: MarkJar | null;
  fetch: MarkFetch | null;
  navigate: (url: string) => void;
  timeoutMs: number;
};

/** ONE HOP OF THE ADMIN'S CHAIN, run as a page of ours loads. No `wt_mark` on the address, or anything in the hop not what the
 *  admin sends → nothing at all, and false. Otherwise: ask the admin's API whether the token is live; only on yes set the mark
 *  here (the cookie on this host's registrable domain and this origin's storage) and, once the cookie reads back, add this
 *  domain to the ones that took it; on a no or a failure set nothing and add nothing; then go on with `location.replace`, to
 *  the next origin or back to the admin, and answer true. Never throws. */
export async function followMarkChain(deps: Partial<MarkChainDeps> = {}): Promise<boolean> {
  try {
    // `globalThis.location` again, never the bare global (the form's build refuses it; see browserHere above).
    const loc = (globalThis as { location?: { search: string; replace: (url: string) => void } }).location;
    const search = 'search' in deps ? String(deps.search ?? '') : loc ? loc.search : '';
    const chain = readMarkChain(search);
    if (!chain) return false;
    const here = deps.here ?? browserHere();
    const store = 'store' in deps ? (deps.store ?? null) : browserStore();
    const jar = 'jar' in deps ? (deps.jar ?? null) : browserJar();
    const fetchFn = 'fetch' in deps ? (deps.fetch ?? null) : typeof fetch === 'undefined' ? null : (fetch as unknown as MarkFetch);
    const navigate =
      deps.navigate ??
      ((url: string) => {
        loc?.replace(url);
      });
    let took = chain.took;
    if (await verifyMarkToken(chain.token, fetchFn, deps.timeoutMs ?? MARK_VERIFY_TIMEOUT_MS)) {
      setMark(true, store, jar, here);
      let stuck = false;
      try {
        stuck = !!jar && cookieMarked(jar.read());
      } catch {
        stuck = false;
      }
      const domain = markDomainOf(here.host) ?? (String(here.host).toLowerCase() === 'localhost' ? 'localhost' : null);
      if (stuck && domain && !took.includes(domain)) took = [...took, domain];
    }
    navigate(nextMarkAddress(chain, took));
    return true;
  } catch {
    return false;
  }
}
